import { isEventDisplayOpen, MAX_DISPLAY_GRACE_DAYS } from './eventCutoff';

export const EVENTS_PAGE_SIZE = 15;

/** Map RPC row to EventCard shape; going_count is authoritative from DB. */
export function normalizeRpcEventRow(row) {
  let venues = row.venues;
  if (venues && typeof venues === 'object' && !Array.isArray(venues)) {
    if (Object.keys(venues).length === 0) venues = null;
  }
  if (Array.isArray(venues) && venues.length > 0) venues = venues[0];
  const gc = typeof row.going_count === 'number' ? row.going_count : Number(row.going_count) || 0;
  let attendees = row.attendees;
  if (attendees == null) attendees = [];
  else if (typeof attendees === 'string') {
    try {
      attendees = JSON.parse(attendees);
    } catch {
      attendees = [];
    }
  }
  if (!Array.isArray(attendees)) attendees = [];

  return {
    ...row,
    venues,
    going_count: gc,
    event_rsvps: [{ count: gc }],
    attendees,
  };
}

/** Shapes a feed or RPC event row for EventCard (venues + display counts). */
export function buildEventForCardFromFeedEvent(event) {
  let venuesData = null;
  if (event.venues) {
    if (Array.isArray(event.venues) && event.venues.length > 0) {
      venuesData = event.venues[0];
    } else if (!Array.isArray(event.venues)) {
      venuesData = event.venues;
    }
  }
  const goingDisplay =
    typeof event.going_count === 'number'
      ? event.going_count
      : Number(event.going_count) || event.event_rsvps?.[0]?.count || 0;
  return {
    ...event,
    event_id: event.event_id,
    event_name: event.event_name,
    event_date: event.event_datetime || event.event_date,
    event_datetime: event.event_datetime,
    event_time: event.event_time || null,
    venues: venuesData,
    event_rsvps: event.event_rsvps,
    rsvp_going_count: goingDisplay,
    attendees: event.attendees || [],
  };
}

export function filterDisplayOpenEvents(events) {
  return events.filter((e) => {
    // A date is enough. This used to require event_datetime and dropped
    // everything without one — which is every multi-day PASS, because
    // Ticketmaster gives no start time for a pass product, and every festival
    // that arrives through discovery, because JamBase publishes dates and not
    // gate times. Camp Flog Gnaw, Neverender, Sway and And Always Forever were
    // all returned by the RPC and then thrown away here.
    //
    // The identical bug was fixed server-side in migration 101, where the feed
    // filtered on `event_datetime >= ...` and NULL >= anything is NULL. This
    // was the second, independent copy of it. isEventDisplayOpen already falls
    // back to event_date via getEventStartDate, so it needed no change.
    if (!e.event_datetime && !e.event_date) return false;
    return isEventDisplayOpen(e);
  });
}

/** Client-side match for p_search (event_name + venue name). */
export function filterEventsBySearchQuery(events, query) {
  const q = query.trim().toLowerCase();
  if (!q) return events;
  return events.filter((e) => {
    const name = (e.event_name || '').toLowerCase();
    const venue = (e.venues?.name || '').toLowerCase();
    return name.includes(q) || venue.includes(q);
  });
}

/**
 * Lower bound for the feed, tied to how long the client will actually SHOW an event.
 *
 * This asked for 12 days of history and it was pure waste: the popular feed sorts by
 * going_count before date, so a well-attended show from last week outranks a quiet one
 * next month, and then filterDisplayOpenEvents throws it away. Measured 2026-09-22:
 * eleven of the first fifteen rows were events from Sept 11. Page one was four usable
 * cards, and the effect that chases more pages when the client filter empties a page had
 * to fire on every cold load to fetch a second one.
 *
 * MAX_DISPLAY_GRACE_DAYS is the widest the display cutoff ever reaches back (a festival,
 * which stays up two days past its start), so anything older cannot survive the filter.
 * Derived rather than hard-coded so the two cannot drift apart. Same window: twelve of
 * the first fifteen rows are now events someone can still go to.
 */
export function defaultRpcMinDateIso() {
  const d = new Date();
  d.setDate(d.getDate() - MAX_DISPLAY_GRACE_DAYS);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/** Stable feed query-key filters — avoids a new cache entry on every remount. */
export function buildFeedQueryFilters({
  sortBy,
  genreFilter,
  debouncedSearch,
  venueTierFilter,
  dateFilter,
  customRange,
  userId,
}) {
  const start = customRange?.[0];
  const end = customRange?.[1];
  return {
    sortBy,
    genreFilter,
    debouncedSearch,
    venueTierFilter,
    dateFilter: dateFilter ?? null,
    customStart: start instanceof Date ? start.toISOString().slice(0, 10) : null,
    customEnd: end instanceof Date ? end.toISOString().slice(0, 10) : null,
    userId: userId ?? null,
  };
}
